/**
 * The app-side halves of the Signal DS `PACalendar*` lift seams.
 *
 * When the PA calendar family graduated into the DS, every place a component
 * reached into the app (a mutation, a permission, the auth store, the
 * scheduling store, a data-bound popover) became a prop. These helpers are the
 * one place the page fills those props from the app, so `calendar-page.tsx`
 * only spreads them.
 */
import type { ReactNode } from 'react';
import type {
  PACalendarDayListProps,
  PACalendarDayListRow,
  PACalendarSlotsPopoverContext,
  PACalendarSubToolbarProps,
} from '@scripsteam/scrips-signal-ds';
import { usePermission } from '@/auth/permission-gate';
import { useUpdateAppointmentStatus } from '@/scheduling/services/use-update-appointment-status';
import { useSchedulingStore } from '@/scheduling/store/scheduling-store';
import { SlotSelectionPopover } from '@/scheduling/components/slot-selection-popover/slot-selection-popover';
import type { CreateAppointmentProfileRequest } from '@/types/scheduling-api';

type DayListSeams = Required<
  Pick<PACalendarDayListProps, 'onStatusChange' | 'statusUpdatePending' | 'canViewBillingTab'>
> & {
  /** The appointment whose status change is in flight: only that row's buttons disable (PM: each row owns its own
   *  mutation). `undefined` when nothing is in flight. */
  statusUpdatePendingId: string | undefined;
};

/**
 * DayList seams: status-changing row actions fire the app's status mutation,
 * the pending flag disables the row buttons, and the Billing-tab overflow item
 * follows the live `Show_Billing_Tab` view permission (the DS fails closed).
 *
 * The pending flag is list-wide for the DS seam; `statusUpdatePendingId` names the row in flight so the app's
 * agenda disables only that row, as PM does (each PM row owns its own mutation).
 */
export function useDayListSeams(): DayListSeams {
  const updateStatus = useUpdateAppointmentStatus();
  const canViewBillingTab = usePermission(['Show_Billing_Tab'], { view: true });
  return {
    onStatusChange: (row: PACalendarDayListRow, code: string) => {
      updateStatus.mutate({
        appointmentId: row.id,
        status: { code, display: code, definition: '' },
      });
    },
    statusUpdatePending: updateStatus.isPending,
    statusUpdatePendingId: updateStatus.isPending ? updateStatus.variables?.appointmentId : undefined,
    canViewBillingTab,
  };
}

type SubToolbarFilters = Pick<
  PACalendarSubToolbarProps,
  | 'selectedStatusCodes'
  | 'onToggleStatusCode'
  | 'onSetStatusCodes'
  | 'onClearStatusCodes'
  | 'selectedInvoiceStatuses'
  | 'onToggleInvoiceStatus'
  | 'onSetInvoiceStatuses'
  | 'onClearInvoiceStatuses'
  | 'selectedLocationIds'
  | 'onToggleLocation'
>;

/** SubToolbar seams: the ten controlled filter props, read from the scheduling store. */
export function useSubToolbarFilters(): SubToolbarFilters {
  const selectedStatusCodes = useSchedulingStore((s) => s.selectedStatusCodes);
  const onToggleStatusCode = useSchedulingStore((s) => s.toggleStatusCode);
  const onSetStatusCodes = useSchedulingStore((s) => s.setSelectedStatusCodes);
  const onClearStatusCodes = useSchedulingStore((s) => s.clearStatusCodes);
  const selectedInvoiceStatuses = useSchedulingStore((s) => s.selectedInvoiceStatuses);
  const onToggleInvoiceStatus = useSchedulingStore((s) => s.toggleInvoiceStatus);
  const onSetInvoiceStatuses = useSchedulingStore((s) => s.setSelectedInvoiceStatuses);
  const onClearInvoiceStatuses = useSchedulingStore((s) => s.clearInvoiceStatuses);
  const selectedLocationIds = useSchedulingStore((s) => s.selectedLocationIds);
  const onToggleLocation = useSchedulingStore((s) => s.toggleLocation);
  return {
    selectedStatusCodes,
    onToggleStatusCode,
    onSetStatusCodes,
    onClearStatusCodes,
    selectedInvoiceStatuses,
    onToggleInvoiceStatus,
    onSetInvoiceStatuses,
    onClearInvoiceStatuses,
    selectedLocationIds,
    onToggleLocation,
  };
}

/**
 * ProviderCard `renderSlotsPopover` seam: wraps the card's "{n} slots" trigger
 * in the app's data-bound SlotSelectionPopover (it fetches free slots). The DS
 * only calls this once practitioner, practice, organization and profile id are
 * all present; the id guard keeps a missing one a plain trigger, not a crash.
 */
export function renderSlotsPopover(
  trigger: ReactNode,
  ctx: PACalendarSlotsPopoverContext<CreateAppointmentProfileRequest>,
): ReactNode {
  if (!ctx.profile.id) return trigger;
  return (
    <SlotSelectionPopover
      practitionerId={ctx.practitionerId}
      practiceId={ctx.practiceId}
      organizationId={ctx.organizationId}
      profileId={ctx.profile.id}
      profileName={ctx.profile.profileName}
      onSelectSlot={ctx.onSelectSlot}
    >
      {trigger}
    </SlotSelectionPopover>
  );
}
